import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

/**
 * PWA Store (Sprint 1 — Ebook PWA, 2026-05-08)
 *
 * Captura o evento `beforeinstallprompt` do navegador uma única vez (em
 * escopo global, no `main.ts`) e expõe `promptInstall()` para qualquer view.
 *
 * Por que store Pinia (e não composable por view):
 *   `beforeinstallprompt` dispara UMA VEZ por sessão. Se um composable é
 *   desmontado antes de o user clicar em "Instalar", o evento se perde.
 *   O store mantém a referência viva durante toda a SPA.
 *
 * Detecta também:
 *   - PWA já instalado (display-mode: standalone) → esconde botão
 *   - iOS Safari → não dispara `beforeinstallprompt`. UI mostra instrução
 *     manual ("Compartilhar → Adicionar à tela de início").
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePwaStore = defineStore('pwa', () => {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const isInstalled = ref(false);
  const isIOS = ref(false);
  const initialized = ref(false);

  const canInstall = computed(() => deferredPrompt.value !== null);

  function detectIOS(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const iOSStandard = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSStandard || iPadOS;
  }

  function detectInstalled(): boolean {
    if (typeof window === 'undefined') return false;
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error: Safari-only API
      window.navigator.standalone === true;
    return isStandalone;
  }

  /**
   * Inicializa os listeners globais. Deve ser chamado UMA vez no `main.ts`
   * antes do `app.mount()`. Idempotente — chamadas múltiplas são no-op.
   */
  function init() {
    if (initialized.value) return;
    initialized.value = true;

    isIOS.value = detectIOS();
    isInstalled.value = detectInstalled();

    // eslint-disable-next-line no-console
    console.info('[PWA Store] Initialized', {
      isIOS: isIOS.value,
      isInstalled: isInstalled.value,
      userAgent: navigator.userAgent,
      standalone: window.matchMedia('(display-mode: standalone)').matches,
    });

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      // eslint-disable-next-line no-console
      console.info('[PWA Store] beforeinstallprompt CAPTURED', {
        platforms: (e as BeforeInstallPromptEvent).platforms,
      });
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null;
      isInstalled.value = true;
      // eslint-disable-next-line no-console
      console.info('[PWA Store] appinstalled');
    });

    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', (e) => {
        isInstalled.value = e.matches;
      });
  }

  /**
   * Tenta disparar o prompt de instalação.
   * @returns 'accepted' | 'dismissed' | 'unavailable' | 'error'
   */
  async function promptInstall(): Promise<
    'accepted' | 'dismissed' | 'unavailable' | 'error'
  > {
    // eslint-disable-next-line no-console
    console.info('[PWA Store] promptInstall called', {
      hasDeferredPrompt: deferredPrompt.value !== null,
      canInstall: canInstall.value,
      isInstalled: isInstalled.value,
      isIOS: isIOS.value,
    });

    if (!deferredPrompt.value) {
      // eslint-disable-next-line no-console
      console.warn(
        '[PWA Store] No deferredPrompt available. Possíveis causas:\n' +
          '  1. Chrome ainda não disparou o evento (manifest/SW indisponível)\n' +
          '  2. Evento já foi consumido em tentativa anterior\n' +
          '  3. Modo responsive do DevTools limita o prompt\n' +
          '  4. Site já está instalado (verifique chrome://apps)\n' +
          '\nUse o ícone nativo do Chrome na barra de URL (⬇) como fallback.',
      );
      return 'unavailable';
    }
    try {
      await deferredPrompt.value.prompt();
      const { outcome } = await deferredPrompt.value.userChoice;
      // eslint-disable-next-line no-console
      console.info('[PWA Store] User choice:', outcome);
      // Após uso, o browser invalida o evento — limpar
      deferredPrompt.value = null;
      return outcome;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PWA Store] prompt() error:', err);
      return 'error';
    }
  }

  return {
    canInstall,
    isInstalled,
    isIOS,
    init,
    promptInstall,
  };
});
